/**
 * Resolves raw ship defs against the asset manifest (asset contract §2.5, §2.9): eye point, fallback
 * collider radius, hull half extents and weapon mounts with ship-space muzzle poses. Pure and headless: it reads the parsed
 * `assets.json` entries only, never a GLB, so Vitest and the sim share one code path.
 */
import { rotateVec, type Quat, type Vec3 } from '../core/math'
import type { AssetsFile, ModelEntry, SocketEntry } from './assetSchema'
import { bandOfLength } from './bands'
import { toShipDef, type Muzzle, type ResolvedMount, type ResolvedTurret, type ShipAssetData, type ShipDef, type ShipDefRaw, type WeaponsData } from './schemas'

/** A hull-collider ship's fallback ball radius is its bounds radius × this (the ball sits inside the silhouette). */
export const HULL_BALL_FACTOR = 0.85
const MUZZLE = /^HP_MUZZLE_(\d+)$/

function quatOf(s: SocketEntry): Quat {
  return { x: s.quaternion[0], y: s.quaternion[1], z: s.quaternion[2], w: s.quaternion[3] }
}

/** Hamilton product a × b (apply b, then a). */
export function mulQuat(a: Quat, b: Quat): Quat {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  }
}

function normalize(v: Vec3): Vec3 {
  const len = Math.hypot(v.x, v.y, v.z)
  return len > 0 ? { x: v.x / len, y: v.y / len, z: v.z / len } : { x: 0, y: 0, z: -1 }
}

/**
 * A module muzzle in ship space: the ship's HP socket pose composed with the module's HP_MUZZLE_n pose.
 * The direction is the composed frame's local −Z (asset contract §2.5).
 */
export function composeMuzzle(mount: SocketEntry, muzzle: SocketEntry): Muzzle {
  const q = quatOf(mount)
  const offset = rotateVec(q, muzzle.position[0], muzzle.position[1], muzzle.position[2], { x: 0, y: 0, z: 0 })
  const pos = { x: mount.position[0] + offset.x, y: mount.position[1] + offset.y, z: mount.position[2] + offset.z }
  const dir = normalize(rotateVec(mulQuat(q, quatOf(muzzle)), 0, 0, -1, { x: 0, y: 0, z: 0 }))
  return { pos, dir }
}

function modelOf(assets: AssetsFile, id: string, shipId: string): ModelEntry {
  const entry = assets.models.find((m) => m.id === id)
  if (!entry) throw new Error(`Ship ${shipId}: asset ${id} is missing from assets.json`)
  return entry
}

function socketOf(entry: ModelEntry, name: string, shipId: string): SocketEntry {
  const s = entry.sockets.find((k) => k.name === name)
  if (!s) throw new Error(`Ship ${shipId}: socket ${name} is missing on asset ${entry.id}`)
  return s
}

/** The manifest-derived fields of one ship def. Throws naming the ship id and the missing socket, module or weapon. */
export function resolveShipAssets(raw: ShipDefRaw, assets: AssetsFile, weapons: WeaponsData): ShipAssetData {
  const ship = modelOf(assets, raw.assetId, raw.id)
  if (ship.class !== 'ship') throw new Error(`Ship ${raw.id}: asset ${ship.id} is a ${ship.class}, not a ship`)

  const eye = socketOf(ship, 'EYE_PILOT', raw.id)
  const eyePoint: [number, number, number] = [
    eye.position[0] + raw.eyeOffset[0],
    eye.position[1] + raw.eyeOffset[1],
    eye.position[2] + raw.eyeOffset[2],
  ]

  const collider = ship.collider
  if (!collider) throw new Error(`Ship ${raw.id}: asset ${ship.id} has no collider`)
  if (collider.kind === 'trimesh') throw new Error(`Ship ${raw.id}: asset ${ship.id} has a trimesh collider (static bodies only)`)
  const colliderRadius = collider.kind === 'ball' ? collider.radius : ship.bounds.radius * HULL_BALL_FACTOR
  const { min, max } = ship.bounds
  // The bounds box where it sits (D-TBD): the docking autopilot puts its centre on the bay axis and the pad.
  const hullHalfExtents: [number, number, number] = [(max[0] - min[0]) / 2, (max[1] - min[1]) / 2, (max[2] - min[2]) / 2]
  const hullCentre: [number, number, number] = [(max[0] + min[0]) / 2, (max[1] + min[1]) / 2, (max[2] + min[2]) / 2]

  let torpedoRounds = 0
  const mounts: ResolvedMount[] = raw.loadout.map((item) => {
    const socket = socketOf(ship, item.socket, raw.id)
    const module = assets.models.find((m) => m.id === item.module)
    if (!module) throw new Error(`Ship ${raw.id}: module ${item.module} on socket ${item.socket} is missing from assets.json`)
    if (module.class !== 'hardpoint') throw new Error(`Ship ${raw.id}: module ${item.module} on socket ${item.socket} is not a hardpoint`)
    const weapon = weapons.weapons.findIndex((w) => w.id === item.weapon)
    if (weapon < 0) throw new Error(`Ship ${raw.id}: weapon ${item.weapon} on socket ${item.socket} is missing from weapons.json`)
    const def = weapons.weapons[weapon]
    if (def.kind === 'torpedo') torpedoRounds += def.rounds
    const muzzleSockets = module.sockets
      .filter((s) => MUZZLE.test(s.name))
      .sort((a, b) => Number(MUZZLE.exec(a.name)?.[1]) - Number(MUZZLE.exec(b.name)?.[1]))
    if (muzzleSockets.length === 0) throw new Error(`Ship ${raw.id}: module ${item.module} on socket ${item.socket} has no HP_MUZZLE_ sockets`)
    return {
      weapon,
      group: item.group,
      slot: item.slot,
      socket: item.socket,
      module: item.module,
      muzzles: muzzleSockets.map((m) => composeMuzzle(socket, m)),
    }
  })

  const turretMounts: ResolvedTurret[] = (raw.turrets ?? []).map((item) => {
    const socket = socketOf(ship, item.socket, raw.id)
    const weapon = weapons.weapons.findIndex((w) => w.id === item.weapon)
    const def = weapons.weapons[weapon]
    if (def === undefined || def.kind !== 'projectile' || def.turret === undefined) {
      throw new Error(`Ship ${raw.id}: turret ${item.weapon} on socket ${item.socket} is not a turret weapon in weapons.json`)
    }
    const [fx, fy, fz] = item.facing
    return {
      weapon,
      socket: item.socket,
      pos: { x: socket.position[0], y: socket.position[1], z: socket.position[2] },
      facing: normalize({ x: fx, y: fy, z: fz }),
      barrels: item.barrels ?? 1,
    }
  })

  // M10 P1d: the size band is the raw override, else the asset's bounds length along Z (the ship's long axis).
  const sizeBand = raw.band ?? bandOfLength(ship.bounds.max[2] - ship.bounds.min[2])

  return { eyePoint, colliderRadius, hullHalfExtents, hullCentre, mounts, torpedoRounds: Math.min(torpedoRounds, 255), turretMounts, sizeBand }
}

/** Resolves every raw def into its runtime ShipDef. */
export function resolveShips(raws: readonly ShipDefRaw[], assets: AssetsFile, weapons: WeaponsData): ShipDef[] {
  return raws.map((raw) => toShipDef(raw, resolveShipAssets(raw, assets, weapons)))
}
